// --- src/App.tsx ---
import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { getUserFriendlyError } from './utils/errorHelper';
import { eventBus, EVENT_TYPES } from './utils/eventBus';
import { safeParseArray } from './utils/safeParse';
import { setupGlobalErrorHandlers } from './utils/globalErrorHandler';
import { logger } from './services/logger';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import EmailGenerator from './components/EmailGenerator';
import DataImporter from './components/DataImporter';
import ScannerInterface from './components/ScannerInterface';
import Reconciliation from './components/Reconciliation';
import Settings from './components/Settings';
import Profile from './components/Profile';
import ForgotPassword from './components/ForgotPassword';
import UpdatePassword from './components/UpdatePassword';
import LabelPrinter from './components/LabelPrinter';
import UserManagement from './components/UserManagement';
import { PrintQuantityDialog } from './components/PrintQuantityDialog';
import { PosHistory } from './components/PosHistory';
import { SuspendedCarts } from './components/SuspendedCarts';
import BossDashboard from './pages/BossDashboard';
import {
  AppView,
  SupplierMap,
  InventoryMap,
  SupplierProduct,
  InventoryItem,
  Permissions,
  ScannerState,
  Company,
} from './components/types';
import { supabase } from './lib/supabaseClient';
import { supabaseService } from './services/supabaseService';
import { syncService } from './services/syncService';
import { companyService } from './services/companyService';
import { DEFAULT_COLORS } from './constants/colors';
import { Loader2, LogIn, Lock, User, UserPlus, ScanBarcode, AlertCircle } from 'lucide-react';
import { SupportModal } from './components/SupportModal';
import { ErrorBoundary } from './components/ErrorBoundary';
import { VersionUpdateBanner } from './components/VersionUpdateBanner';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useInventorySync, useOfflineQueue, useNetworkStatus } from './hooks';
import { APP_VERSION } from './constants/version';

/**
 * SCANNER V2 MIGRATION FLAG
 *
 * Toggle between old (V1, 2400 lines) and new (V2, 200 lines) scanner logic.
 *
 * To enable V2:
 *   1. Open browser console
 *   2. Run: localStorage.setItem('scanner_use_v2', 'true')
 *   3. Refresh page
 *
 * To disable V2 (revert to V1):
 *   1. Run: localStorage.setItem('scanner_use_v2', 'false')
 *   2. Refresh page
 *
 * Default: V1 (for safety, until V2 is fully tested)
 */
const USE_SCANNER_V2 = localStorage.getItem('scanner_use_v2') === 'true';

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<AppView>(AppView.DASHBOARD);
  const { isNewVersionAvailable } = useVersionCheck();

  // Log scanner version on mount
  useEffect(() => {
    console.log(
      `%c🔧 Scanner Version: ${USE_SCANNER_V2 ? 'V2 (New, Refactored)' : 'V1 (Legacy)'}`,
      'background: #4CAF50; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );
    if (USE_SCANNER_V2) {
      console.log(
        '%cℹ️ Scanner V2 is EXPERIMENTAL. To revert: localStorage.setItem("scanner_use_v2", "false")',
        'color: #FF9800; font-weight: bold;'
      );
    } else {
      console.log(
        '%cℹ️ To enable Scanner V2: localStorage.setItem("scanner_use_v2", "true")',
        'color: #2196F3;'
      );
    }
  }, []);

  // NEW: Auto-sync and queue monitoring
  // 🔥 ATOMBIZTOSAN MŰKÖDŐ NETWORK STATUS
  const networkStatus = useNetworkStatus();
  const {
    lastSyncTime: _lastSyncTime,
    isSyncing: isSyncingNew,
    migratedItemsCount,
  } = useInventorySync();
  const { queueLength } = useOfflineQueue();

  // Setup global error handlers on app mount
  useEffect(() => {
    const cleanup = setupGlobalErrorHandlers();
    return cleanup;
  }, []);

  // Expose network status and sync service globally for debugging and access from services
  useEffect(() => {
    (window as any).__networkStatus = networkStatus;
    (window as any).__syncService = syncService;
    logger.debug('[App] Network status updated', networkStatus, 'App');

    // 🔥 CRITICAL: Stop Supabase auto-refresh when offline to prevent auth token spam
    if (!networkStatus.isConnectedToBackend && networkStatus.connectionQuality === 'offline') {
      try {
        // Stop auto-refresh to prevent hundreds of failed token refresh attempts
        (supabase.auth as any).stopAutoRefresh?.();
        logger.info('[App] Supabase auto-refresh STOPPED (offline mode)', undefined, 'App');
      } catch (error) {
        // Ignore error if stopAutoRefresh doesn't exist
      }
    } else if (networkStatus.isConnectedToBackend && networkStatus.connectionQuality === 'good') {
      try {
        // Restart auto-refresh when back online
        (supabase.auth as any).startAutoRefresh?.();
        logger.info('[App] Supabase auto-refresh STARTED (online mode)', undefined, 'App');
      } catch (error) {
        // Ignore error if startAutoRefresh doesn't exist
      }
    }
  }, [networkStatus]);

  // 🔥 AUTO-SYNC: Trigger sync when backend connection is restored
  useEffect(() => {
    // Skip initial mount (lastCheckTime === 0)
    if (networkStatus.lastCheckTime === 0) return;

    // If backend connection was just restored
    if (networkStatus.isConnectedToBackend && networkStatus.connectionQuality !== 'offline') {
      // Check if there are pending items
      syncService.getQueueSize().then((queueSize) => {
        if (queueSize > 0) {
          logger.info(
            `[App] 🔄 Connection restored, auto-syncing ${queueSize} pending items`,
            undefined,
            'App'
          );

          // Show toast notification
          toast(`🔄 Kapcsolat helyreállt!\n\n${queueSize} tétel szinkronizálása folyamatban...`, {
            duration: 3000,
            icon: '🌐',
            style: {
              background: '#4CAF50',
              color: 'white',
              fontWeight: 'bold',
            },
          });

          // Trigger sync
          syncService.processQueue().catch((error) => {
            logger.error('[App] Auto-sync failed', error, 'App');
          });
        }
      });
    }
  }, [networkStatus.isConnectedToBackend]);

  // 🔥 OFFLINE TOAST: Notify when connection is lost
  useEffect(() => {
    // Skip initial mount (lastCheckTime === 0)
    if (networkStatus.lastCheckTime === 0) return;

    // If backend connection was just lost
    if (!networkStatus.isConnectedToBackend && networkStatus.connectionQuality === 'offline') {
      toast(
        `⚠️ Kapcsolat megszakadt!\n\nNe aggódj, minden adat biztonságban van.\nAz offline módban végzett műveletek automatikusan szinkronizálódnak, amikor helyreáll a kapcsolat.`,
        {
          duration: 5000,
          icon: '📡',
          style: {
            background: '#FF9800',
            color: 'white',
            fontWeight: 'bold',
          },
        }
      );
    }
  }, [networkStatus.isConnectedToBackend, networkStatus.connectionQuality]);

  // Show migration toast (once)
  useEffect(() => {
    if (migratedItemsCount !== null && migratedItemsCount > 0) {
      toast.success(
        `✅ ${migratedItemsCount} offline tétel átmigrálva az új rendszerbe (IndexedDB)`,
        {
          duration: 5000,
          icon: '🔄',
        }
      );
    }
  }, [migratedItemsCount]);

  // 🔥 TOAST NOTIFICATION: Offline checkout queued
  useEffect(() => {
    const handleCheckoutQueued = (data: any) => {
      console.log('[App] Offline checkout queued', data);
      toast(
        `📦 Tranzakció offline módban elmentve (${data.itemCount} termék)
        
Szinkronizálás a kapcsolat helyreállásakor.`,
        {
          duration: 8000,
          icon: '🔄',
          style: {
            background: '#FF9800',
            color: 'white',
            fontWeight: 'bold',
          },
        }
      );
    };

    const handleSyncProgress = (data: any) => {
      if (data.action === 'checkout_queued') {
        handleCheckoutQueued(data);
      }
    };

    const unsubscribe = eventBus.on(EVENT_TYPES.SYNC_PROGRESS, handleSyncProgress);

    return () => {
      unsubscribe();
    };
  }, []);

  // PHASE 3, SPRINT 3.1: Listen to SYNC_COMPLETED and refresh UI
  useEffect(() => {
    const handleSyncCompleted = (payload: any) => {
      const progress = payload as { total: number; successful: number; failed: number };
      logger.info('[App] Sync completed, refreshing data...', progress, 'App');

      // Skip if checkout in progress
      if (checkoutInProgressRef.current) {
        logger.info('[App] Skipping loadCloudData (checkout in progress)', undefined, 'App');
        return;
      }

      // Refresh inventory data after sync (use ref to avoid stale closure)
      loadCloudDataRef.current?.().catch((err) => {
        logger.error('[App] loadCloudData failed after sync', err, 'App');
      });
    };

    const unsubscribe = eventBus.on(EVENT_TYPES.SYNC_COMPLETED, handleSyncCompleted);

    return () => {
      unsubscribe();
    };
  }, []); // loadCloudDataRef is a stable ref, always points to latest function

  // PHASE 3, SPRINT 3.1: Listen to INVENTORY_UPDATED and refresh UI
  useEffect(() => {
    // Debounce tracker (stored globally to persist across re-renders)
    if (!(window as any)._lastInventoryReload) {
      (window as any)._lastInventoryReload = 0;
    }

    const handleInventoryUpdated = (payload: any) => {
      const data = payload as { ean: string; source?: string };
      logger.info('[App] Inventory updated via EventBus', data, 'App');

      // Skip if checkout in progress
      if (checkoutInProgressRef.current) {
        logger.info('[App] Skipping loadCloudData (checkout in progress)', undefined, 'App');
        return;
      }

      // Debounce: Only reload if last reload was > 2 seconds ago
      // EXCEPTION: Realtime updates always trigger reload (critical for UI consistency)
      const now = Date.now();
      const timeSinceLastReload = now - (window as any)._lastInventoryReload;
      const isRealtime = data.source === 'realtime';

      // ✅ HOTFIX: BYPASS DEBOUNCE FOR REALTIME EVENTS
      if (isRealtime || timeSinceLastReload > 2000) {
        (window as any)._lastInventoryReload = now;
        logger.info(
          `[App] Triggering loadCloudData (source: ${data.source || 'unknown'}, debounce bypassed: ${isRealtime})`,
          undefined,
          'App'
        );
        // Use ref to always call the latest loadCloudData (avoids stale closure with filterMode)
        loadCloudDataRef.current?.().catch((err) => {
          logger.error('[App] loadCloudData failed after inventory update', err, 'App');
        });
      } else {
        logger.debug(
          `[App] Debounced: Skipping reload (last reload was ${timeSinceLastReload}ms ago, source: ${data.source || 'unknown'})`,
          undefined,
          'App'
        );
      }
    };

    const unsubscribe = eventBus.on(EVENT_TYPES.INVENTORY_UPDATED, handleInventoryUpdated);

    return () => {
      unsubscribe();
    };
  }, []); // loadCloudDataRef is a stable ref, always points to latest function

  // Show Scanner V2 toast (once, if enabled)
  useEffect(() => {
    if (USE_SCANNER_V2) {
      toast(
        '🔬 Scanner V2 (Experimental) aktív\nRevert: localStorage.setItem("scanner_use_v2", "false")',
        {
          duration: 10000,
          icon: '⚗️',
          style: {
            background: '#FF9800',
            color: 'white',
            fontWeight: 'bold',
          },
        }
      );
    }
  }, []); // Run once on mount

  const [scannerState, setScannerState] = useState<ScannerState>({
    barcodeInput: '',
    lastScanned: null,
    newName: '',
    newPrice: '',
    newSellingPrice: '',
    newMinQuantity: '',
    newIsWeighable: false,
    newUnitWeight: '',
    newUnit: 'db',
    scanMode: 'COUNT',
    transactionQty: 1,
    // POS/Cart state - lifted up for persistence
    cart: [],
    isCheckoutModalOpen: false,
    checkoutStep: 'SELECT_TYPE',
    selectedPaymentType: 'CASH',
    discountPercent: 0,
    pendingAdjustment: null,
  });

  // State-ek az adatoknak
  const [supplierData, setSupplierData] = useState<SupplierMap>({});
  const [inventoryData, setInventoryData] = useState<InventoryMap>({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(50); // Fixed page size

  // Search state (lifted from Dashboard for server-side search)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<'ALL' | 'LOW_STOCK' | 'SHOPPING_LIST' | 'NO_PRICE'>(
    'ALL'
  );

  // Debounce search term to avoid excessive re-renders (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page to 1 when search term changes (both when starting and clearing search)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // CRITICAL: Flag to prevent loadCloudData from overwriting checkout state
  const checkoutInProgressRef = React.useRef<boolean>(false);
  const checkoutTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // AbortController for cancelling stale loadCloudData fetches (replaces loadCloudDataInProgressRef)
  const loadAbortControllerRef = React.useRef<AbortController | null>(null);
  const mountedRef = React.useRef<boolean>(true);

  // Ref to always hold the latest loadCloudData function (avoids stale closure in early useEffects).
  // The function wraps loadCloudData with AbortController for automatic request cancellation.
  const loadCloudDataRef = React.useRef<((overridePage?: number) => Promise<void>) | null>(null);

  // Auth state timeout ref (memory leak prevention)
  const authTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Realtime subscription management
  const realtimeRetryCountRef = React.useRef<number>(0);
  const realtimeChannelRef = React.useRef<any>(null);
  const realtimeReconnectTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_RETRY_ATTEMPTS = 5;
  const RECONNECT_DELAY = 2000; // 2 seconds
  const LABELS_PER_SHEET = 44; // 4 oszlop × 11 sor (48mm × 25mm címkék)

  // Print Queue State - stores items to be printed as labels
  // Initialize from localStorage if available
  const [printQueue, setPrintQueue] = useState<
    Array<{ item: InventoryItem; product: SupplierProduct }>
  >(() => {
    const saved = localStorage.getItem('leltarmester_print_queue');
    const parsed = safeParseArray(saved, []);

    if (parsed.length > 0) {
      // Convert scannedAt strings back to Date objects and validate structure
      return parsed
        .filter((entry: any) => entry && typeof entry === 'object' && entry.item && entry.product)
        .map((entry: any) => ({
          ...entry,
          item: {
            ...entry.item,
            scannedAt: entry.item.scannedAt ? new Date(entry.item.scannedAt) : new Date(),
          },
        }));
    }

    return [];
  });

  // Admin status state
  const [isAdmin, setIsAdmin] = useState(false);

  // User permissions state (company-level)
  const [permissions, setPermissions] = useState<Permissions | null>(null);

  // Current user profile with granular permissions
  const [userProfile, setUserProfile] = useState<{
    role: 'admin' | 'employee';
    access_labels?: boolean;
    access_email?: boolean;
    access_import?: boolean;
    access_pos?: boolean;
    access_boss_dashboard?: boolean;
    full_name?: string;
  } | null>(null);

  // Initialization error state
  const [initError, setInitError] = useState<string | null>(null);

  // Company branding state
  const [companyBranding, setCompanyBranding] = useState<Company | null>(null);
  const [_brandingLoading, setBrandingLoading] = useState(true);
  const [preLoginBranding, setPreLoginBranding] = useState<Company | null>(null);

  // Debug state for admin role checking
  const [debugRole, setDebugRole] = useState<string | null>('loading...');
  const [debugError, setDebugError] = useState<string | null>(null);

  // Debug panel visibility state
  const [showDebug, setShowDebug] = useState(false);

  // Auth State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState<'LOGIN' | 'REGISTER'>('LOGIN');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // BIZTOS MEGOLDÁS: A .com domaint minden rendszer elfogadja
  const SYSTEM_DOMAIN = 'example.com';

  // Dark Mode State Management
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('theme');
    if (saved === 'dark' || saved === 'light') {
      return saved === 'dark';
    }
    // Check system preference if no saved preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Apply dark mode class to document (runs on mount and when isDarkMode changes)
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // --- PRE-LOGIN BRANDING LOADER (Domain-based) ---
  // Run IMMEDIATELY on mount, regardless of session
  useEffect(() => {
    const loadPreLoginBranding = async () => {
      try {
        const hostname = window.location.hostname;
        const branding = await companyService.getBrandingByHostname(hostname);
        setPreLoginBranding(branding);

        // Inject CSS variables for pre-login screen
        const root = document.documentElement;
        const primaryColor = branding.ui_settings.primary_color || DEFAULT_COLORS.primary_color;
        const secondaryColor =
          branding.ui_settings.secondary_color || DEFAULT_COLORS.secondary_color;
        const accentColor = branding.ui_settings.accent_color || DEFAULT_COLORS.accent_color;

        // Light mode colors
        const bgLight =
          branding.ui_settings.background_color_light ||
          branding.ui_settings.background_color ||
          DEFAULT_COLORS.background_color_light;
        const cardLight =
          branding.ui_settings.card_background_light ||
          branding.ui_settings.card_background ||
          DEFAULT_COLORS.card_background_light;

        // Dark mode colors
        const bgDark =
          branding.ui_settings.background_color_dark ||
          branding.ui_settings.background_color ||
          DEFAULT_COLORS.background_color_dark;
        const cardDark =
          branding.ui_settings.card_background_dark ||
          branding.ui_settings.card_background ||
          DEFAULT_COLORS.card_background_dark;

        // Set base colors
        root.style.setProperty('--color-primary', primaryColor);
        root.style.setProperty('--color-secondary', secondaryColor);
        root.style.setProperty('--color-accent', accentColor);

        // Set light/dark mode variables
        root.style.setProperty('--color-bg-light', bgLight);
        root.style.setProperty('--color-bg-dark', bgDark);
        root.style.setProperty('--color-card-light', cardLight);
        root.style.setProperty('--color-card-dark', cardDark);

        // Set CSS override for pre-login
        const styleId = 'pre-login-background-override';
        let style = document.getElementById(styleId) as HTMLStyleElement;
        if (!style) {
          style = document.createElement('style');
          style.id = styleId;
          document.head.appendChild(style);
        }
        style.textContent = `
          :root {
            --color-background: var(--color-bg-light);
            --color-card-bg: var(--color-card-light);
          }
          
          .dark {
            --color-background: var(--color-bg-dark);
            --color-card-bg: var(--color-card-dark);
          }
          
          body {
            background-color: var(--color-secondery) !important;
          }
          
          .bg-card {
            background-color: var(--color-card-bg) !important;
          }
        `;
      } catch (error) {
        console.error('[App] Error loading pre-login branding:', error);
        // Use defaults
        const root = document.documentElement;
        root.style.setProperty('--color-primary', DEFAULT_COLORS.primary_color);
        root.style.setProperty('--color-secondary', DEFAULT_COLORS.secondary_color);
        root.style.setProperty('--color-accent', DEFAULT_COLORS.accent_color);
        root.style.setProperty('--color-bg-light', DEFAULT_COLORS.background_color_light);
        root.style.setProperty('--color-bg-dark', DEFAULT_COLORS.background_color_dark);
        root.style.setProperty('--color-card-light', DEFAULT_COLORS.card_background_light);
        root.style.setProperty('--color-card-dark', DEFAULT_COLORS.card_background_dark);
      }
    };

    loadPreLoginBranding();
  }, []); // Run once on mount, before login

  // --- COMPANY BRANDING LOADER (Theme Engine) ---
  useEffect(() => {
    const loadCompanyBranding = async () => {
      // Helper function to check if color is light
      const isLightColor = (color: string): boolean => {
        try {
          // Remove # if present
          const hex = color.replace('#', '');
          if (hex.length !== 6) return false;
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          // Calculate luminance
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          return luminance > 0.5;
        } catch {
          return false;
        }
      };

      if (!session) {
        // Set default colors for login screen
        const root = document.documentElement;
        root.style.setProperty('--color-primary', DEFAULT_COLORS.primary_color);
        root.style.setProperty('--color-secondary', DEFAULT_COLORS.secondary_color);
        root.style.setProperty('--color-accent', DEFAULT_COLORS.accent_color);

        // Light/Dark mode variables
        root.style.setProperty('--color-bg-light', DEFAULT_COLORS.background_color_light);
        root.style.setProperty('--color-bg-dark', DEFAULT_COLORS.background_color_dark);
        root.style.setProperty('--color-card-light', DEFAULT_COLORS.card_background_light);
        root.style.setProperty('--color-card-dark', DEFAULT_COLORS.card_background_dark);

        root.style.setProperty('--navbar-text-color', '#ffffff');
        setBrandingLoading(false);
        return;
      }

      try {
        const branding = await companyService.getCompanyBranding();
        setCompanyBranding(branding);

        // Inject CSS variables into :root
        const root = document.documentElement;
        const primaryColor = branding.ui_settings.primary_color || DEFAULT_COLORS.primary_color;
        const secondaryColor =
          branding.ui_settings.secondary_color || DEFAULT_COLORS.secondary_color;
        const accentColor = branding.ui_settings.accent_color || DEFAULT_COLORS.accent_color;

        // Light mode colors (with fallback to old fields or defaults)
        const bgLight =
          branding.ui_settings.background_color_light ||
          branding.ui_settings.background_color ||
          DEFAULT_COLORS.background_color_light;
        const cardLight =
          branding.ui_settings.card_background_light ||
          branding.ui_settings.card_background ||
          DEFAULT_COLORS.card_background_light;

        // Dark mode colors (with fallback to old fields or defaults)
        const bgDark =
          branding.ui_settings.background_color_dark ||
          branding.ui_settings.background_color ||
          DEFAULT_COLORS.background_color_dark;
        const cardDark =
          branding.ui_settings.card_background_dark ||
          branding.ui_settings.card_background ||
          DEFAULT_COLORS.card_background_dark;

        // Set base colors
        root.style.setProperty('--color-primary', primaryColor);
        root.style.setProperty('--color-secondary', secondaryColor);
        root.style.setProperty('--color-accent', accentColor);

        // Set light/dark mode variables
        root.style.setProperty('--color-bg-light', bgLight);
        root.style.setProperty('--color-bg-dark', bgDark);
        root.style.setProperty('--color-card-light', cardLight);
        root.style.setProperty('--color-card-dark', cardDark);

        // Set text color based on secondary background
        root.style.setProperty(
          '--navbar-text-color',
          isLightColor(secondaryColor) ? '#000000' : '#ffffff'
        );

        // Add CSS override with intelligent dark mode switching
        const styleId = 'dynamic-background-override';
        let style = document.getElementById(styleId) as HTMLStyleElement;
        if (!style) {
          style = document.createElement('style');
          style.id = styleId;
          document.head.appendChild(style);
        }
        style.textContent = `
          :root {
            --color-background: var(--color-bg-light);
            --color-card-bg: var(--color-card-light);
          }
          
          /* Amikor a .dark osztály aktív a html elemen */
          .dark {
            --color-background: var(--color-bg-dark);
            --color-card-bg: var(--color-card-dark);
          }
          
          body {
            background-color: var(--color-background) !important;
          }
          
          .bg-card {
            background-color: var(--color-card-bg) !important;
          }
        `;
      } catch (error) {
        console.error('[App] Error loading company branding:', error);
        // Use defaults
        const root = document.documentElement;
        root.style.setProperty('--color-primary', DEFAULT_COLORS.primary_color);
        root.style.setProperty('--color-secondary', DEFAULT_COLORS.secondary_color);
        root.style.setProperty('--color-accent', DEFAULT_COLORS.accent_color);
        root.style.setProperty('--color-bg-light', DEFAULT_COLORS.background_color_light);
        root.style.setProperty('--color-bg-dark', DEFAULT_COLORS.background_color_dark);
        root.style.setProperty('--color-card-light', DEFAULT_COLORS.card_background_light);
        root.style.setProperty('--color-card-dark', DEFAULT_COLORS.card_background_dark);
        root.style.setProperty('--navbar-text-color', '#ffffff');

        // Add CSS override for defaults too
        const styleId = 'dynamic-background-override';
        let style = document.getElementById(styleId) as HTMLStyleElement;
        if (!style) {
          style = document.createElement('style');
          style.id = styleId;
          document.head.appendChild(style);
        }
        style.textContent = `
          :root {
            --color-background: var(--color-bg-light);
            --color-card-bg: var(--color-card-light);
          }
          
          .dark {
            --color-background: var(--color-bg-dark);
            --color-card-bg: var(--color-card-dark);
          }
          
          body {
            background-color: var(--color-background) !important;
          }
          
          .bg-card {
            background-color: var(--color-card-bg) !important;
          }
        `;
      } finally {
        setBrandingLoading(false);
      }
    };

    loadCompanyBranding();
  }, [session]);

  // Persist printQueue to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('leltarmester_print_queue', JSON.stringify(printQueue));
    } catch (error) {
      console.warn('Failed to save print queue to localStorage:', error);
    }
  }, [printQueue]);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + D: Toggle debug panel
      if (modKey && e.key === 'd') {
        e.preventDefault();
        setShowDebug((prev) => !prev);
        return;
      }

      // Ctrl/Cmd + K: Focus search on Dashboard
      if (modKey && e.key === 'k') {
        e.preventDefault();
        if (currentView === AppView.DASHBOARD) {
          const searchInput = document.getElementById('dashboard-search-input') as HTMLInputElement;
          searchInput?.focus();
          searchInput?.select();
        }
        return;
      }

      // Ctrl/Cmd + R: Toggle Rapid Mode on Scanner (only if on scanner view)
      if (modKey && e.key === 'r') {
        e.preventDefault();
        if (currentView === AppView.INVENTORY_SCAN || currentView === AppView.DASHBOARD) {
          // Trigger rapid mode toggle by dispatching a custom event
          // The ScannerInterface component will listen for this
          window.dispatchEvent(new CustomEvent('toggleRapidMode'));
        }
        return;
      }

      // Escape: Close modals
      if (e.key === 'Escape') {
        e.preventDefault();

        // Close edit modal on Dashboard
        const editModal = document.getElementById('edit-modal');
        if (editModal) {
          const closeButton = editModal.querySelector(
            'button[aria-label="Bezárás"]'
          ) as HTMLButtonElement;
          if (closeButton) {
            closeButton.click();
            return;
          }
        }

        // Close Support Modal (check for the modal backdrop)
        const supportModalBackdrop = document.querySelector('[class*="backdrop-blur-sm"]');
        if (supportModalBackdrop) {
          const closeButton = supportModalBackdrop.querySelector(
            'button[aria-label="Bezárás"]'
          ) as HTMLButtonElement;
          if (closeButton) {
            closeButton.click();
            return;
          }
        }

        // Close confirmation dialogs
        const confirmDialog = document.querySelector('[class*="ConfirmDialog"]');
        if (confirmDialog) {
          const cancelButton = confirmDialog.querySelector(
            'button:not([class*="bg-red"]):not([class*="bg-amber"]):not([class*="bg-blue"])'
          ) as HTMLButtonElement;
          if (cancelButton && cancelButton.textContent?.includes('Mégse')) {
            cancelButton.click();
            return;
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentView]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const newValue = !prev;
      // Immediately apply to DOM for instant feedback
      const root = document.documentElement;
      if (newValue) {
        root.classList.add('dark');
        localStorage.setItem('theme', 'dark');
      } else {
        root.classList.remove('dark');
        localStorage.setItem('theme', 'light');
      }
      return newValue;
    });
  };

  // Check for UpdatePassword hash and dashboard route on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#update-password') {
      // UpdatePassword will handle its own rendering
    } else if (hash === '#dashboard' || hash === '#/dashboard') {
      // Set view to BOSS_DASHBOARD if hash matches
      setCurrentView(AppView.BOSS_DASHBOARD);
    }
  }, []);

  // Listen for hash changes (for dashboard route)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#dashboard' || hash === '#/dashboard') {
        setCurrentView(AppView.BOSS_DASHBOARD);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Check user role function - non-blocking, always sets isAdmin to false on error
  const checkUserRole = useCallback(async () => {
    try {
      setDebugRole('checking...');
      setDebugError(null);

      const role = await supabaseService.getCurrentUserRole();
      setDebugRole(role || 'null');

      const adminStatus = role === 'admin';
      setIsAdmin(adminStatus);

      // User role checked
    } catch (error: any) {
      console.error('Error checking user role:', error);
      setDebugError(error.message || String(error));
      setDebugRole('error');
      setIsAdmin(false);
      // Don't throw - this is non-critical
    }
  }, []);

  const loadCloudData = useCallback(
    async (overridePage?: number, signal?: AbortSignal) => {
      // CRITICAL: Skip if checkout is in progress or recently completed
      if (checkoutInProgressRef.current) {
        console.log('[loadCloudData] ⚠️ SKIPPING - checkout in progress or recently completed');
        return;
      }

      // RACE CONDITION PROTECTION: Skip if component is unmounted
      if (!mountedRef.current) {
        console.log('[loadCloudData] ⚠️ SKIPPING - component is unmounted');
        return;
      }

      // Use overridePage if provided (e.g. filterMode change resets to page 1),
      // otherwise use current page from state
      const effectivePage = overridePage ?? currentPage;

      try {
        console.log(
          `[loadCloudData] Starting data load (page=${effectivePage}, filter=${filterMode})...`
        );

        // NOTE: Permissions and userProfile are loaded by onAuthStateChange
        // We don't load them here to avoid duplicate calls and race conditions

        // Load inventory data
        const { supplierMap, inventoryMap, totalCount } = await supabaseService.fetchAllData(
          effectivePage,
          pageSize,
          debouncedSearchTerm,
          filterMode
        );

        // ABORT CHECK: If this request was cancelled (newer request in flight), discard results
        if (signal?.aborted) {
          console.log('[loadCloudData] ⚠️ ABORTED - newer request superseded this one');
          return;
        }

        const supplierCount = Object.keys(supplierMap).length;
        const inventoryCount = Object.keys(inventoryMap).length;

        console.log(
          `[loadCloudData] ✅ Received data: ${supplierCount} suppliers, ${inventoryCount} inventory items, totalCount: ${totalCount}`
        );

        if (supplierCount === 0 && inventoryCount === 0) {
          console.warn('[loadCloudData] ⚠️ WARNING: No data received from database!');
        }

        // CRITICAL: Double-check checkout flag before updating state
        if (checkoutInProgressRef.current) {
          console.log('[loadCloudData] ⚠️ SKIPPING state update - checkout flag set during fetch');
          return;
        }

        // RACE CONDITION PROTECTION: Check if component is still mounted before state update
        if (!mountedRef.current) {
          console.log(
            '[loadCloudData] ⚠️ SKIPPING state update - component unmounted during fetch'
          );
          return;
        }

        // ABORT CHECK again before state update (in case abort happened during processing)
        if (signal?.aborted) {
          console.log('[loadCloudData] ⚠️ ABORTED before state update');
          return;
        }

        // STEP 4: Ghost Data Protection - Determine update strategy
        const isSearchOrFilterMode = debouncedSearchTerm?.trim() || filterMode !== 'ALL';

        if (isSearchOrFilterMode) {
          // SEARCH/FILTER MODE: REPLACE Strategy
          // Completely replace state with filtered results to prevent "ghost items"
          console.log(
            `[loadCloudData] 🔍 SEARCH/FILTER MODE (${debouncedSearchTerm ? 'SEARCH' : filterMode}): Replacing state with filtered results`
          );
          setSupplierData(supplierMap);
          setInventoryData(inventoryMap);
        } else {
          // NORMAL MODE: SMART MERGE Strategy (Pagination Mode)
          // Merge state for pagination support with strict "Last Writer Wins" timestamp comparison
          console.log(
            '[loadCloudData] 📄 NORMAL MODE: Smart merge for pagination (Last Writer Wins)'
          );

          // Merge supplier data (simple merge - no timestamp logic needed)
          setSupplierData((prev) => ({ ...prev, ...supplierMap }));

          // CRITICAL: Merge inventory data with strict "Last Writer Wins" timestamp comparison
          // This prevents stale DB data from overwriting recent scans/checkouts (race condition protection)
          setInventoryData((prevInventory) => {
            const merged: InventoryMap = { ...inventoryMap };

            // For each item in previous state, check if we should keep it
            Object.keys(prevInventory).forEach((ean) => {
              const prevItem = prevInventory[ean];
              const dbItem = inventoryMap[ean];

              // If item exists in previous state but not in DB result, keep it
              // (might be a new item, pagination issue, or item with quantity=0 that was filtered out)
              if (!dbItem && prevItem) {
                merged[ean] = prevItem;
                console.log(
                  `[loadCloudData] Keeping item from prev state (not in DB result): ${ean}`
                );
              }
              // If item exists in both, use strict "Last Writer Wins" strategy based on timestamp
              else if (dbItem && prevItem) {
                const prevTime = prevItem.scannedAt?.getTime() || 0;
                const dbTime = dbItem.scannedAt?.getTime() || 0;

                // ✅ HOTFIX: Clock Skew Buffer (2 minutes = 120000ms)
                const CLOCK_SKEW_BUFFER_MS = 120000; // 2 minutes tolerance

                if (prevTime > dbTime + CLOCK_SKEW_BUFFER_MS) {
                  merged[ean] = prevItem;
                } else {
                  merged[ean] = dbItem;
                }
              }
            });

            console.log(`[loadCloudData] Merge complete: ${Object.keys(merged).length} items`);
            return merged;
          });
        }

        setTotalItems(totalCount);
        // [App] Data load complete
      } catch (error: any) {
        // Don't log abort errors - they're expected when switching filters quickly
        if (signal?.aborted) return;

        console.error('[loadCloudData] Error loading data:', error);
        // Don't throw - allow app to continue with empty data
        // NEVER overwrite existing permissions on error
        if (!permissions) {
          setPermissions({
            hasImportModule: false,
            hasLabelModule: false,
            hasEmailModule: false,
            hasProcurementModule: false,
            hasPosModule: false,
            hasBossDashboard: false,
          });
        }
      }
    },
    [currentPage, pageSize, debouncedSearchTerm, filterMode, permissions, userProfile]
  );

  // Keep the ref always pointing to the latest loadCloudData (for early useEffects that can't depend on it directly).
  // The ref wraps loadCloudData with AbortController so event handlers (sync, realtime) automatically
  // cancel any in-flight request before starting a new one — preventing out-of-order state updates.
  React.useEffect(() => {
    loadCloudDataRef.current = async (overridePage?: number) => {
      // Cancel any in-flight request before starting a new one
      if (loadAbortControllerRef.current) {
        loadAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      loadAbortControllerRef.current = controller;

      await loadCloudData(overridePage, controller.signal);
    };
  }, [loadCloudData]);

  // Track previous filterMode to detect changes and reset page
  const prevFilterModeRef = React.useRef(filterMode);

  // UNIFIED EFFECT: Load data when session/page/search/filter changes
  // Combines filterMode page-reset + data loading into ONE effect to prevent race conditions.
  // Uses AbortController to cancel stale requests when dependencies change quickly.
  useEffect(() => {
    if (!session) return;

    // Detect filterMode change → reset page to 1
    let effectivePage = currentPage;
    if (filterMode !== prevFilterModeRef.current) {
      console.log(
        `[useEffect] filterMode changed: ${prevFilterModeRef.current} → ${filterMode}, resetting to page 1`
      );
      prevFilterModeRef.current = filterMode;
      effectivePage = 1;
      setCurrentPage(1);
    }

    // Cancel any in-flight request before starting a new one
    if (loadAbortControllerRef.current) {
      loadAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    loadAbortControllerRef.current = controller;

    console.log(
      `[useEffect] Loading data (page=${effectivePage}, filter=${filterMode}, search=${debouncedSearchTerm || '(none)'})`
    );

    loadCloudData(effectivePage, controller.signal).catch((err) => {
      if (!controller.signal.aborted) {
        console.error('[useEffect] ❌ loadCloudData failed:', err);
      }
    });

    // Cleanup: abort on unmount or before next effect run
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, currentPage, pageSize, debouncedSearchTerm, filterMode]);

  // 1. Session ellenőrzése - Refactored with guaranteed loading exit
  // Version logging on app start
  useEffect(() => {
    console.log(
      `%c 🚀 LeltárMester AI v${APP_VERSION} elindítva `,
      'background: #2563eb; color: #fff; font-weight: bold; padding: 4px; border-radius: 4px;'
    );
  }, []); // Only run once on mount

  // CRITICAL: This only checks session, permissions are loaded in onAuthStateChange
  useEffect(() => {
    let mounted = true;

    const initApp = async () => {
      try {
        // [App] Starting initialization

        // 1. Check Session - This is fast and should not block
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (mounted) {
          setSession(session);
          // [App] Session found
        }

        if (session) {
          // [App] Session exists - permissions will be loaded in onAuthStateChange
        } else {
          // [App] No session found
          setIsAdmin(false);
        }
      } catch (err: any) {
        console.error('[initApp] Critical Init Error:', err);
        if (mounted) {
          setInitError(err.message || 'Ismeretlen hiba történt indításkor.');
          setIsAdmin(false);
        }
      } finally {
        // ALWAYS Remove Loading Screen - This MUST run regardless of errors
        // Don't wait for permissions - they'll load in onAuthStateChange
        // [App] Setting loading to false
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initApp();

    // Cleanup function
    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Separate useEffect for auth state changes
  useEffect(() => {
    let mounted = true;

    // Auth Listener
    // CRITICAL: This is where permissions and userProfile are loaded when user logs in
    // This MUST complete successfully and set permissions BEFORE loadCloudData runs
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        console.log(
          '[onAuthStateChange] Event:',
          event,
          'Session:',
          !!session,
          'Current permissions:',
          !!permissions
        );
        setSession(session);

        if (session) {
          // TASK 1: Cache user session for offline authentication
          try {
            if (session.user) {
              localStorage.setItem(
                'offline_user',
                JSON.stringify({
                  id: session.user.id,
                  email: session.user.email,
                  cached_at: new Date().toISOString(),
                })
              );
              console.log('[App] User cached for offline authentication:', session.user.id);
            }
          } catch (error) {
            console.error('[App] Failed to cache user session:', error);
          }
          // Only reload permissions on SIGNED_IN event or if we don't have permissions yet
          // Don't reload on TOKEN_REFRESHED to prevent permission loss
          const shouldReloadPermissions = event === 'SIGNED_IN' || !permissions;

          if (shouldReloadPermissions) {
            console.log('[onAuthStateChange] Loading permissions and user profile...');

            const defaultPermissions = {
              hasImportModule: false,
              hasLabelModule: false,
              hasEmailModule: false,
              hasProcurementModule: false,
              hasPosModule: false,
              hasBossDashboard: false,
            };

            // Load asynchronously - don't await, let it complete in background
            Promise.all([
              supabaseService.getUserPermissions().catch((err) => {
                console.warn('[onAuthStateChange] getUserPermissions failed, using defaults:', err);
                return defaultPermissions;
              }),
              supabaseService.getCurrentUserProfile().catch((err) => {
                console.warn('[onAuthStateChange] getCurrentUserProfile failed:', err);
                return null;
              }),
            ])
              .then(([perms, profile]) => {
                if (!mounted) return;

                console.log('[onAuthStateChange] Permissions loaded:', perms);
                console.log('[onAuthStateChange] User profile loaded:', profile);

                // CRITICAL: Only update permissions if we got valid data with at least one true value
                // OR if we don't have permissions yet (first load)
                const hasValidPermissions = perms && Object.values(perms).some((v) => v === true);

                if (hasValidPermissions) {
                  // We got real permissions (at least one is true)
                  console.log('[onAuthStateChange] Setting permissions (valid data)');
                  setPermissions(perms);
                } else if (!permissions) {
                  // Only set defaults if we don't have permissions yet (first load)
                  console.log('[onAuthStateChange] Setting permissions (first load, defaults)');
                  setPermissions(perms);
                } else {
                  // Keep existing permissions, don't overwrite with defaults
                  console.log(
                    '[onAuthStateChange] Keeping existing permissions, new ones are all false'
                  );
                }

                if (profile) {
                  setUserProfile({
                    role: profile.role,
                    access_labels: profile.access_labels ?? false,
                    access_email: profile.access_email ?? false,
                    access_import: profile.access_import ?? false,
                    access_pos: profile.access_pos ?? false,
                    access_boss_dashboard: profile.access_boss_dashboard ?? false,
                    full_name: (profile as any).full_name,
                  });
                }

                // Load user role (non-blocking)
                checkUserRole().catch((err) => {
                  console.warn('[onAuthStateChange] checkUserRole failed (non-critical):', err);
                });
              })
              .catch((err) => {
                console.error('[onAuthStateChange] Unexpected error:', err);
                // Only set defaults if we don't have permissions yet
                if (mounted && !permissions) {
                  setPermissions(defaultPermissions);
                }
              });
          } else {
            console.log(
              '[onAuthStateChange] Skipping permission reload (event:',
              event,
              ', has permissions:',
              !!permissions,
              ')'
            );
          }

          // CRITICAL: Always trigger loadCloudData when session exists
          // The useEffect with session dependency will handle the actual loading
          // But we need to ensure it runs even if session was already set
          console.log('[onAuthStateChange] Session exists, ensuring loadCloudData will run...');

          // Use setTimeout to ensure this runs after state updates
          // Clear any pending auth timeout to prevent memory leak
          if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
          }
          authTimeoutRef.current = setTimeout(() => {
            if (mounted) {
              const hasData =
                Object.keys(inventoryData).length > 0 || Object.keys(supplierData).length > 0;
              if (!hasData) {
                console.log('[onAuthStateChange] No data loaded yet, triggering loadCloudData...');
                loadCloudDataRef.current?.().catch((err) => {
                  console.error('[onAuthStateChange] loadCloudData failed:', err);
                });
              } else {
                console.log('[onAuthStateChange] Data already loaded, skipping loadCloudData');
              }
            }
            authTimeoutRef.current = null;
          }, 100); // Small delay to ensure state is updated
        } else {
          console.log('[onAuthStateChange] Session cleared, resetting state');
          setSupplierData({});
          setInventoryData({});
          setIsAdmin(false);
          setPermissions(null);
          setUserProfile(null);
          setCurrentPage(1);
          setTotalItems(0);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      // Clear auth timeout to prevent memory leak
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Real-time inventory updates using Supabase Realtime with auto-reconnect
  // CRITICAL: Trust RLS (Row Level Security) - if we receive an event, it's relevant to our company
  useEffect(() => {
    if (!session) {
      // Clean up any existing channel if session is lost
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      if (realtimeReconnectTimeoutRef.current) {
        clearTimeout(realtimeReconnectTimeoutRef.current);
        realtimeReconnectTimeoutRef.current = null;
      }
      realtimeRetryCountRef.current = 0;
      return;
    }

    const setupSubscription = async () => {
      // Clear any existing reconnect timeout
      if (realtimeReconnectTimeoutRef.current) {
        clearTimeout(realtimeReconnectTimeoutRef.current);
        realtimeReconnectTimeoutRef.current = null;
      }

      // CRITICAL: Clean up existing channel BEFORE creating new one to prevent CHANNEL_ERROR loops
      if (realtimeChannelRef.current) {
        console.log('[Realtime] Cleaning up existing channel before creating new subscription');
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
        // Small delay to ensure cleanup completes (prevents race conditions)
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      console.log('[Realtime] Setting up inventory subscription...', {
        attempt: realtimeRetryCountRef.current + 1,
        maxAttempts: MAX_RETRY_ATTEMPTS,
      });

      // Get company_id for filtering at channel level (saves CPU with 6000+ items)
      let companyId: string | null = null;
      try {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', session.user.id)
          .single();
        companyId = userProfile?.company_id || null;
        console.log('[Realtime] Company ID for filtering:', companyId || 'NOT FOUND');
      } catch (error) {
        console.warn(
          '[Realtime] Failed to get company_id, falling back to RLS-only filtering:',
          error
        );
      }

      const schemaName = import.meta.env.VITE_SUPABASE_SCHEMA || 'public';
      console.log('[Realtime] Using schema:', schemaName);
      console.log(
        companyId
          ? `[Realtime] Subscribing to inventory_items changes filtered by company_id: ${companyId}`
          : '[Realtime] Subscribing to ALL inventory_items changes (RLS ensures company-level access)'
      );

      const inventoryChannel = supabase
        .channel(`inventory_changes_${session.user.id}_${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: '*', // INSERT, UPDATE, DELETE
            schema: schemaName,
            table: 'inventory_items',
            ...(companyId && { filter: `company_id=eq.${companyId}` }), // Filter at channel level if company_id available
          },
          (payload) => {
            // ROBUST: Wrap event handler in try-catch to prevent crashes
            try {
              console.log('[Realtime] 📨 Raw payload received:', payload);

              const record = payload.new || payload.old;
              const userId = (record as any)?.user_id;
              const isOwnChange = userId && userId === session.user.id;

              console.log(
                '[Realtime] Event received - User:',
                userId || 'unknown',
                isOwnChange ? '(own)' : '(other)'
              );

              // CRITICAL: Only skip OWN updates during checkout (to avoid double-counting)
              // ALWAYS process updates from OTHER users, even during checkout
              if (isOwnChange && checkoutInProgressRef.current) {
                console.log(
                  '[Realtime] ⏭️ Skipping own update during checkout (will update after checkout completes)'
                );
                return;
              }

              if (!isOwnChange) {
                console.log(
                  '[Realtime] ✅ Processing update from other user - immediate sync required'
                );
              }

              // Handle different event types - IMMEDIATE STATE UPDATE
              if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
                const newRecord = payload.new as any;
                const ean = newRecord.ean;

                if (!ean) {
                  console.warn('[Realtime] No EAN in payload, skipping');
                  return;
                }

                console.log(
                  `[Realtime] 📦 Inventory updated from remote - EAN: ${ean}, Quantity: ${newRecord.quantity}, User: ${userId || 'unknown'}`
                );

                // ✅ HOTFIX: Update BOTH inventoryData AND supplierData states
                setInventoryData((prev) => {
                  try {
                    const existingItem = prev[ean];

                    // HOTFIX: Map ALL database fields to frontend format (not just quantity)
                    // CRITICAL: Sync local timestamp with server timestamp to fix clock skew
                    const updatedItem = {
                      ean: ean,
                      quantity: newRecord.quantity ?? 0,
                      // ✅ CLOCK SKEW FIX: Use server timestamp (updated_at or last_scanned_at)
                      scannedAt: newRecord.last_scanned_at
                        ? new Date(newRecord.last_scanned_at)
                        : newRecord.updated_at
                          ? new Date(newRecord.updated_at)
                          : existingItem?.scannedAt || new Date(),
                      min_quantity: newRecord.min_quantity ?? existingItem?.min_quantity ?? null,
                      on_shopping_list:
                        newRecord.on_shopping_list ?? existingItem?.on_shopping_list ?? false,
                      // ✅ HOTFIX: Update ALL price fields from DB (fixes UI not updating after edit)
                      netPrice: newRecord.net_price
                        ? Number(newRecord.net_price)
                        : existingItem?.netPrice,
                      netSellingPrice: newRecord.net_selling_price
                        ? Number(newRecord.net_selling_price)
                        : existingItem?.netSellingPrice,
                      vatRate: newRecord.vat_rate
                        ? Number(newRecord.vat_rate)
                        : existingItem?.vatRate,
                      // ✅ HOTFIX: Update unit/weight fields
                      unit: newRecord.unit || existingItem?.unit || 'db',
                      isWeighable: newRecord.is_weighable ?? existingItem?.isWeighable ?? false,
                      unitWeight: newRecord.unit_weight
                        ? Number(newRecord.unit_weight)
                        : existingItem?.unitWeight,
                    };

                    console.log(
                      `[Realtime] ✅ Updated FULL inventory for ${ean}: qty=${updatedItem.quantity} (was ${existingItem?.quantity ?? 0}), net_price=${updatedItem.netPrice}, timestamp=${updatedItem.scannedAt.toISOString()}`
                    );

                    return {
                      ...prev,
                      [ean]: updatedItem,
                    };
                  } catch (stateError) {
                    console.error('[Realtime] Error updating inventory state:', stateError);
                    // Return previous state to prevent crash
                    return prev;
                  }
                });

                // ✅ CRITICAL: Also update supplierData with price changes (fixes Dashboard price display)
                setSupplierData((prev) => {
                  try {
                    const existingSupplier = prev[ean];
                    if (!existingSupplier) {
                      // If supplier doesn't exist yet, create it with full data
                      return {
                        ...prev,
                        [ean]: {
                          ean: ean,
                          name: newRecord.name || 'Ismeretlen',
                          sku: newRecord.sku || '',
                          netPrice: newRecord.net_price ? Number(newRecord.net_price) : 0,
                          netSellingPrice: newRecord.net_selling_price
                            ? Number(newRecord.net_selling_price)
                            : 0,
                          sellingPrice: newRecord.selling_price
                            ? Number(newRecord.selling_price)
                            : 0,
                          vatRate: newRecord.vat_rate ? Number(newRecord.vat_rate) : 27,
                          unit: newRecord.unit || 'db',
                          isWeighable: newRecord.is_weighable ?? false,
                          unitWeight: newRecord.unit_weight
                            ? Number(newRecord.unit_weight)
                            : undefined,
                        },
                      };
                    }

                    // Update existing supplier with new price data
                    const updatedSupplier = {
                      ...existingSupplier,
                      name: newRecord.name || existingSupplier.name,
                      sku: newRecord.sku || existingSupplier.sku,
                      netPrice: newRecord.net_price
                        ? Number(newRecord.net_price)
                        : existingSupplier.netPrice,
                      netSellingPrice: newRecord.net_selling_price
                        ? Number(newRecord.net_selling_price)
                        : existingSupplier.netSellingPrice,
                      sellingPrice: newRecord.selling_price
                        ? Number(newRecord.selling_price)
                        : existingSupplier.sellingPrice,
                      vatRate: newRecord.vat_rate
                        ? Number(newRecord.vat_rate)
                        : existingSupplier.vatRate,
                      unit: newRecord.unit || existingSupplier.unit,
                      isWeighable: newRecord.is_weighable ?? existingSupplier.isWeighable,
                      unitWeight: newRecord.unit_weight
                        ? Number(newRecord.unit_weight)
                        : existingSupplier.unitWeight,
                    };

                    console.log(
                      `[Realtime] ✅ Updated supplier data for ${ean}: net_price=${updatedSupplier.netPrice}`
                    );

                    return {
                      ...prev,
                      [ean]: updatedSupplier,
                    };
                  } catch (stateError) {
                    console.error('[Realtime] Error updating supplier state:', stateError);
                    // Return previous state to prevent crash
                    return prev;
                  }
                });

                // PHASE 3, SPRINT 3.1: Emit event to notify other components
                eventBus.emit(EVENT_TYPES.INVENTORY_UPDATED, {
                  ean,
                  quantity: newRecord.quantity,
                  source: 'realtime',
                  eventType: payload.eventType,
                });
              } else if (payload.eventType === 'DELETE') {
                const oldRecord = payload.old as any;
                const ean = oldRecord?.ean;

                if (!ean) {
                  console.warn('[Realtime] No EAN in DELETE payload, skipping');
                  return;
                }

                console.log(`[Realtime] 📦 Inventory item deleted - EAN: ${ean}`);
                setInventoryData((prev) => {
                  try {
                    if (prev[ean]) {
                      return {
                        ...prev,
                        [ean]: {
                          ...prev[ean],
                          quantity: 0,
                        },
                      };
                    }
                    return prev;
                  } catch (stateError) {
                    console.error(
                      '[Realtime] Error updating inventory state on DELETE:',
                      stateError
                    );
                    return prev;
                  }
                });

                // PHASE 3, SPRINT 3.1: Emit event to notify other components
                eventBus.emit(EVENT_TYPES.INVENTORY_UPDATED, {
                  ean,
                  quantity: 0,
                  source: 'realtime',
                  eventType: 'DELETE',
                  deleted: true,
                });
              }
            } catch (error) {
              // CRITICAL: Log error but don't crash the app
              console.error('[Realtime] Error processing payload:', error);
              // Don't throw - allow app to continue functioning
            }
          }
        )
        .subscribe((status) => {
          console.log('[Realtime] Subscription status:', status);

          if (status === 'SUBSCRIBED') {
            console.log(
              '[Realtime] ✅ Successfully subscribed to inventory changes (RLS ensures company-level access)'
            );
            // Reset retry count on successful subscription
            realtimeRetryCountRef.current = 0;
            realtimeChannelRef.current = inventoryChannel;
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            console.error(
              `[Realtime] ❌ Subscription ${status.toLowerCase()} - attempting reconnect...`
            );

            // Auto-reconnect logic
            if (realtimeRetryCountRef.current < MAX_RETRY_ATTEMPTS) {
              realtimeRetryCountRef.current += 1;

              console.log(
                `[Realtime] 🔄 Reconnecting in ${RECONNECT_DELAY}ms (attempt ${realtimeRetryCountRef.current}/${MAX_RETRY_ATTEMPTS})...`
              );

              // Schedule reconnect
              realtimeReconnectTimeoutRef.current = setTimeout(() => {
                setupSubscription();
              }, RECONNECT_DELAY);
            } else {
              console.error(
                `[Realtime] ❌ Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Stopping auto-reconnect.`
              );
              console.error(
                '[Realtime] ⚠️ Realtime sync disabled. Please refresh the page to restore sync.'
              );
              // Don't clear the channel ref - allow manual refresh to restore
            }
          }
        });

      // Store channel reference
      realtimeChannelRef.current = inventoryChannel;
    };

    // Initial subscription setup
    setupSubscription();

    // Cleanup subscription on unmount or session change
    return () => {
      console.log('[Realtime] Cleaning up inventory subscription');

      // Clear reconnect timeout
      if (realtimeReconnectTimeoutRef.current) {
        clearTimeout(realtimeReconnectTimeoutRef.current);
        realtimeReconnectTimeoutRef.current = null;
      }

      // Remove channel
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }

      // Reset retry count
      realtimeRetryCountRef.current = 0;
    };
  }, [session, currentPage, pageSize]);

  // Component unmount cleanup for race condition protection
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      console.log('[App] Component unmounting - setting mountedRef to false');
    };
  }, []);

  // Undo state for deleted items
  const [undoAction, setUndoAction] = useState<{
    ean: string;
    supplier: SupplierProduct;
    inventory: InventoryItem;
  } | null>(null);

  const handleDeleteItem = async (ean: string) => {
    try {
      // Store item data before deletion for undo
      const itemData = {
        ean,
        supplier: supplierData[ean],
        inventory: inventoryData[ean],
      };

      // Allow deletion even if only one part exists (supplier or inventory)
      // This handles edge cases where data might be incomplete
      if (!itemData.supplier && !itemData.inventory) {
        toast.error('A tétel adatai nem találhatók!');
        return;
      }

      await supabaseService.deleteItem(ean);

      // Helyi state frissítése (hogy ne kelljen újratölteni az egészet)
      const newInventory = { ...inventoryData };
      const newSupplier = { ...supplierData };

      // Delete from inventory if exists
      if (newInventory[ean]) {
        delete newInventory[ean];
        setInventoryData(newInventory);
      }

      // Delete from supplier if exists
      if (newSupplier[ean]) {
        delete newSupplier[ean];
        setSupplierData(newSupplier);
      }

      // Store undo action
      setUndoAction({
        ean,
        supplier: itemData.supplier,
        inventory: itemData.inventory,
      });

      // Show success toast with undo button
      toast.success(
        (t) => (
          <div className="flex items-center gap-3">
            <span>Tétel törölve</span>
            <button
              onClick={() => {
                handleUndo(t.id);
              }}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded font-bold text-sm transition"
            >
              Visszavonás
            </button>
          </div>
        ),
        { duration: 5000 }
      );
    } catch (e) {
      const friendlyError = getUserFriendlyError(e);
      toast.error(friendlyError);
      console.error(e);
    }
  };

  const handleUndo = async (toastId?: string) => {
    if (!undoAction) return;

    try {
      await supabaseService.recreateItem({
        supplier: undoAction.supplier,
        inventory: {
          ean: undoAction.ean,
          quantity: undoAction.inventory.quantity,
          scannedAt: undoAction.inventory.scannedAt,
        },
      });

      // Restore local state
      setSupplierData((prev) => ({
        ...prev,
        [undoAction.ean]: undoAction.supplier,
      }));
      setInventoryData((prev) => ({
        ...prev,
        [undoAction.ean]: undoAction.inventory,
      }));

      // Dismiss the toast
      if (toastId) {
        toast.dismiss(toastId);
      }

      toast.success('Művelet visszavonva');
      setUndoAction(null);
    } catch (e) {
      const friendlyError = getUserFriendlyError(e);
      toast.error(friendlyError);
      console.error(e);
    }
  };

  const handleUpdateItem = async (
    ean: string,
    newData: {
      name: string;
      price: number;
      selling_price?: number;
      netPrice?: number;
      netSellingPrice?: number;
      vatRate?: number;
      quantity: number;
      unit?: 'db' | 'm' | 'kg';
      is_weighable?: boolean;
      unit_weight?: number;
      min_quantity?: number | null;
    }
  ) => {
    try {
      // 1. Kiszámoljuk a helyes Bruttó árat a Nettóból (ha van Nettó)
      // Ez a biztonsági háló, ha a Dashboard véletlenül a régi Bruttót küldte volna
      const vat = newData.vatRate ?? 27;
      let finalGrossPrice = newData.price;

      if (newData.netPrice !== undefined && newData.netPrice > 0) {
        finalGrossPrice = Math.round(newData.netPrice * (1 + vat / 100) * 100) / 100;
      }

      // ✅ HOTFIX: Await editItem to get server timestamp back
      const editResult = await supabaseService.editItem(ean, {
        name: newData.name,
        price: finalGrossPrice, // A frissen számolt Bruttót küldjük
        selling_price: newData.selling_price,
        netPrice: newData.netPrice,
        netSellingPrice: newData.netSellingPrice,
        vatRate: newData.vatRate,
        quantity: newData.quantity,
        unit: newData.unit,
        isWeighable: newData.is_weighable,
        unitWeight: newData.unit_weight,
        minQuantity: newData.min_quantity,
      });

      // ✅ HOTFIX: Use server timestamp from response (fixes clock skew)
      const serverTimestamp = editResult?.serverTimestamp
        ? new Date(editResult.serverTimestamp)
        : new Date();

      console.log(
        `[handleUpdateItem] Using server timestamp: ${serverTimestamp.toISOString()} (local would be: ${new Date().toISOString()})`
      );

      // ✅ CRITICAL: Update local state IMMEDIATELY (even if DB update succeeded)
      // This ensures UI reflects the change instantly, regardless of Realtime delays
      setInventoryData((prev) => ({
        ...prev,
        [ean]: {
          ...prev[ean],
          ean,
          quantity: newData.quantity,
          // ✅ CRITICAL: Use server timestamp instead of Date.now() to prevent clock skew
          scannedAt: serverTimestamp,
          unit: newData.unit || 'db',
          min_quantity:
            newData.min_quantity !== undefined
              ? newData.min_quantity
              : (prev[ean]?.min_quantity ?? null),
          netPrice: newData.netPrice,
          netSellingPrice: newData.netSellingPrice,
          vatRate: newData.vatRate,
        },
      }));

      setSupplierData((prev) => ({
        ...prev,
        [ean]: {
          ...prev[ean],
          ean,
          name: newData.name,

          // JAVÍTÁS: Kezeljük az undefined esetet (?? 0)
          netPrice: newData.netPrice ?? prev[ean]?.netPrice ?? 0,
          netSellingPrice: newData.netSellingPrice ?? prev[ean]?.netSellingPrice ?? 0,

          // JAVÍTÁS: A frissen számolt Bruttót mentjük el a state-be is!
          price: finalGrossPrice,
          sellingPrice: newData.selling_price || prev[ean]?.sellingPrice || 0,

          vatRate: newData.vatRate ?? 27,

          sku: prev[ean]?.sku || '',
          unit: newData.unit || 'db',
          isWeighable: newData.is_weighable,
          unitWeight: newData.unit_weight,
        },
      }));

      // ✅ SUCCESS: Show success toast
      toast.success('✅ Módosítások sikeresen mentve!');
    } catch (e) {
      const friendlyError = getUserFriendlyError(e);
      toast.error(`❌ Hiba mentés közben: ${friendlyError}`);
      console.error('[handleUpdateItem] Error:', e);

      // ⚠️ CRITICAL: DO NOT update state on error - let user retry
      // The Realtime handler will update state if DB was actually modified
    }
  };

  // Print Queue Dialog State
  const [printQuantityDialog, setPrintQuantityDialog] = useState<{
    isOpen: boolean;
    ean: string | null;
  }>({ isOpen: false, ean: null });

  // Print Queue Functions
  const addToPrintQueue = (ean: string) => {
    // Megnyitjuk a dialogot a mennyiség megadásához
    setPrintQuantityDialog({ isOpen: true, ean });
  };

  const handlePrintQuantityConfirm = (quantity: number) => {
    if (!printQuantityDialog.ean) return;

    const inventoryItem = inventoryData[printQuantityDialog.ean];
    const supplierItem = supplierData[printQuantityDialog.ean];

    if (!inventoryItem || !supplierItem) {
      toast.error('A termék nem található az adatbázisban!');
      setPrintQuantityDialog({ isOpen: false, ean: null });
      return;
    }

    // Kiszámoljuk, hány hely van még az aktuális íven
    const currentSheetPosition = printQueue.length % LABELS_PER_SHEET;
    const remainingOnCurrentSheet = LABELS_PER_SHEET - currentSheetPosition;

    // Hozzáadjuk a címkéket (flat lista - ugyanaz a termék többször is szerepelhet)
    const newLabels = Array.from({ length: quantity }, () => ({
      item: inventoryItem,
      product: supplierItem,
    }));

    setPrintQueue((prev) => [...prev, ...newLabels]);

    // Információ a felhasználónak
    if (quantity > remainingOnCurrentSheet) {
      toast.success(
        `"${supplierItem.name}": ${remainingOnCurrentSheet} db az aktuális ívre, ` +
          `${quantity - remainingOnCurrentSheet} db a következő ívre`
      );
    } else {
      toast.success(`"${supplierItem.name}" hozzáadva: ${quantity} db`);
    }

    setPrintQuantityDialog({ isOpen: false, ean: null });
  };

  const removeFromPrintQueue = (ean: string) => {
    setPrintQueue((prev) => prev.filter((q) => q.item.ean !== ean));
  };

  // Pagination functions
  const goToNextPage = () => {
    const totalPages = Math.ceil(totalItems / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Fetch all items for current filter (for print/export full list)
  const fetchAllItemsForPrint = useCallback(async () => {
    const { supplierMap, inventoryMap } = await supabaseService.fetchAllData(
      1,
      totalItems,
      debouncedSearchTerm,
      filterMode
    );
    return { supplierMap, inventoryMap };
  }, [totalItems, debouncedSearchTerm, filterMode]);

  // --- Authentikáció Kezelése ---

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);

    const emailToUse = `${username.trim()}@${SYSTEM_DOMAIN}`;

    try {
      if (authMode === 'LOGIN') {
        // --- BEJELENTKEZÉS ---
        const { data, error } = await supabase.auth.signInWithPassword({
          email: emailToUse,
          password,
        });

        if (error) throw error;

        // BIZTONSÁGI ELLENŐRZÉS (Hogy Rentalos user ne léphessen be ide)
        const userAppType = data.user?.user_metadata?.app_type;
        // Ha van bélyeg, de NEM LELTARMESTER, akkor kidobjuk
        if (userAppType && userAppType !== 'LELTARMESTER') {
          await supabase.auth.signOut();
          throw new Error('Ez a fiók nem a LeltárMesterhez tartozik!');
        }
      } else {
        // --- REGISZTRÁCIÓ ---
        const { error } = await supabase.auth.signUp({
          email: emailToUse,
          password,
          options: {
            data: {
              username: username.trim(),
              app_type: 'LELTARMESTER', // <--- Itt a bélyeg!
            },
          },
        });

        if (error) throw error;

        toast.success(`Sikeres regisztráció! Üdv a csapatban, ${username}!`);
        setAuthMode('LOGIN');
      }
    } catch (err: any) {
      if (err.message.includes('Invalid login')) {
        setAuthError('Hibás felhasználónév vagy jelszó!');
      } else if (err.message.includes('already registered')) {
        setAuthError('Ez a felhasználónév már foglalt!');
      } else {
        setAuthError(err.message);
      }
    } finally {
      setAuthLoading(false);
    }
  };

  // --- Loading Képernyő ---
  if (loading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center text-slate-500 dark:text-gray-400 gap-4"
        style={{ backgroundColor: 'var(--color-background, #f8fafc)' }}
      >
        <Loader2 className="animate-spin text-primary" size={48} />
        <p className="font-medium animate-pulse">LeltárMester betöltése...</p>
      </div>
    );
  }

  // --- Error Screen (if initialization failed) ---
  if (initError && !session) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: 'var(--color-background, #f8fafc)' }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 border border-red-200 dark:border-red-800">
          <div className="text-center">
            <div className="bg-red-100 dark:bg-red-900/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="text-red-600 dark:text-red-400" size={32} />
            </div>
            <h2 className="text-xl font-bold text-red-800 dark:text-red-200 mb-2">Indítási Hiba</h2>
            <p className="text-red-600 dark:text-red-400 mb-6 whitespace-pre-wrap">{initError}</p>
            <button
              onClick={() => {
                setInitError(null);
                setLoading(true);
                window.location.reload();
              }}
              className="px-6 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
            >
              Újratöltés
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Update Password Screen (from email link) ---
  if (window.location.hash === '#update-password') {
    return <UpdatePassword />;
  }

  // --- Bejelentkező Képernyő ---
  if (!session) {
    // Show ForgotPassword if requested
    if (showForgotPassword) {
      return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
    }

    // Extract branding logic to variables at the top of the return
    const displayBranding = preLoginBranding || companyBranding;
    const displayName = displayBranding?.name || 'Solyom barkácsbolt';
    const displaySubtitle =
      displayBranding?.ui_settings.login_subtitle || 'Kereskedelmi és készletkezelő rendszer';
    const logoUrl = displayBranding?.logo_url;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: 'var(--color-background, #f8fafc)' }}
      >
        <div className="bg-card rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div
            className="p-8 text-center"
            style={{ backgroundColor: 'var(--color-secondary, #1e293b)' }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={displayName}
                className="mx-auto h-24 w-24 object-contain mb-4 rounded-lg bg-white p-2"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const iconContainer = e.currentTarget.nextElementSibling;
                  if (iconContainer) {
                    iconContainer.classList.remove('hidden');
                  }
                }}
              />
            ) : null}
            <div
              className={`mx-auto w-16 h-16 rounded-xl flex items-center justify-center mb-4 shadow-lg ${
                logoUrl ? 'hidden' : ''
              }`}
              style={{
                backgroundColor: 'var(--color-primary, #2563eb)',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
              }}
            >
              <ScanBarcode className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">{displayName}</h1>
            <p className="text-slate-400 text-sm mt-2">{displaySubtitle}</p>
          </div>

          <div className="p-8">
            <h2 className="text-xl font-bold text-slate-800 dark:text-gray-100 mb-6 text-center">
              {authMode === 'LOGIN' ? 'Bejelentkezés' : 'Új Fiók Létrehozása'}
            </h2>

            {authError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded border border-red-100 dark:border-red-800 flex items-center gap-2">
                <span className="font-bold">Hiba:</span> {authError}
              </div>
            )}

            <form onSubmit={handleAuth} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                  Felhasználónév
                </label>
                <div className="relative">
                  <User
                    className="absolute left-3 top-3 text-slate-400 dark:text-gray-500"
                    size={18}
                  />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="pl. Raktar01"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                  Jelszó
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3 top-3 text-slate-400 dark:text-gray-500"
                    size={18}
                  />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-lg font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authLoading ? (
                  <Loader2 className="animate-spin" />
                ) : authMode === 'LOGIN' ? (
                  <LogIn size={18} />
                ) : (
                  <UserPlus size={18} />
                )}
                {authMode === 'LOGIN' ? 'Belépés' : 'Regisztráció'}
              </button>
            </form>

            <div className="mt-6 space-y-2 text-center">
              {authMode === 'LOGIN' && (
                <button
                  onClick={() => setShowForgotPassword(true)}
                  className="block w-full text-sm text-slate-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition"
                >
                  Elfelejtetted a jelszavad?
                </button>
              )}
              {/*
              <button 
                onClick={() => { setAuthMode(authMode === 'LOGIN' ? 'REGISTER' : 'LOGIN'); setAuthError(null); }}
                className="text-sm text-slate-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition"
              >
                
                {authMode === 'LOGIN' 
                  ? 'Nincs még fiókod? Regisztrálj!' // TODO: Ezt a gombot nem látszik, és nem is működik
                  : 'Vissza a bejelentkezéshez'}
                
              </button>
              */}
              <div className="text-center text-xs text-slate-400 dark:text-slate-500">
                &copy; okoskeszlet.hu v1.3.0
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ENNEK OTT KELL LENNIE A KÓDBAN:
  const renderView = () => {
    switch (currentView) {
      case AppView.DASHBOARD:
        // SALE mód mindig látható (kassza funkció)
        // POS modul (kosár + checkout) csak akkor működik, ha:
        // 1. Company-nak van POS modulja (permissions.hasPosModule)
        // 2. User admin VAGY van hozzáférése (isAdmin || userProfile?.access_pos)
        const hasPosModule = permissions?.hasPosModule && (isAdmin || userProfile?.access_pos);
        return (
          <ScannerInterface
            inventoryData={inventoryData}
            setInventoryData={setInventoryData}
            supplierData={supplierData}
            setSupplierData={setSupplierData}
            savedState={scannerState}
            setSavedState={setScannerState}
            // SALE mód mindig látható (kassza funkció)
            allowedModes={['SALE', 'RETURN', 'SCRAP', 'INTERNAL']}
            hasPosModule={hasPosModule}
            currentTerminalName={userProfile?.full_name || 'Ismeretlen Pult'}
            onCheckoutStart={() => {
              console.log('[App] Checkout starting - setting checkout flag');
              checkoutInProgressRef.current = true;
              if (checkoutTimeoutRef.current) {
                clearTimeout(checkoutTimeoutRef.current);
                checkoutTimeoutRef.current = null;
              }
            }}
            onCheckoutComplete={() => {
              console.log('[App] Checkout complete - resetting checkout flag immediately');
              // CRITICAL: Always reset checkout flag immediately
              // The timeout was causing the flag to stay true, blocking Realtime updates
              checkoutInProgressRef.current = false;
              if (checkoutTimeoutRef.current) {
                clearTimeout(checkoutTimeoutRef.current);
                checkoutTimeoutRef.current = null;
              }
              // Immediately trigger a data reload after checkout to ensure UI is up-to-date
              loadCloudData().catch((err) => {
                console.error('[App] loadCloudData failed after checkout:', err);
              });
            }}
          />
        );
      case AppView.EMAIL_GENERATOR:
        return <EmailGenerator />; // <--- Itt használjuk!
      case AppView.IMPORT_DATA:
        return (
          <DataImporter
            supplierData={supplierData}
            setSupplierData={setSupplierData}
            reloadData={loadCloudData}
          />
        );
      case AppView.INVENTORY_SCAN:
        return (
          <ScannerInterface
            inventoryData={inventoryData}
            setInventoryData={setInventoryData}
            supplierData={supplierData}
            setSupplierData={setSupplierData}
            savedState={scannerState}
            setSavedState={setScannerState}
            // Csak a kék leltár gomb látszik
            allowedModes={['COUNT']}
            currentTerminalName={userProfile?.full_name || 'Ismeretlen Pult'}
            onCheckoutStart={() => {
              checkoutInProgressRef.current = true;
              if (checkoutTimeoutRef.current) {
                clearTimeout(checkoutTimeoutRef.current);
              }
            }}
            onCheckoutComplete={() => {
              console.log('[App] Checkout complete - blocking loadCloudData for 15 seconds');
              checkoutInProgressRef.current = true;
              if (checkoutTimeoutRef.current) {
                clearTimeout(checkoutTimeoutRef.current);
              }
              checkoutTimeoutRef.current = setTimeout(() => {
                checkoutInProgressRef.current = false;
                console.log(
                  '[App] ✅ Checkout timeout expired (15s) - loadCloudData can run again'
                );
              }, 15000); // 15 seconds - increased to ensure state is stable
            }}
          />
        );
      case AppView.RECONCILIATION:
        return (
          <Reconciliation
            inventoryData={inventoryData}
            supplierData={supplierData}
            isAdmin={isAdmin}
          />
        );
      case AppView.SETTINGS:
        return (
          <Settings
            inventoryData={inventoryData}
            supplierData={supplierData}
            setInventoryData={setInventoryData}
            setSupplierData={setSupplierData}
            isAdmin={isAdmin}
            setView={setCurrentView}
          />
        );
      case AppView.PROFILE:
        return <Profile />;
      case AppView.LABEL_PRINTER:
        return (
          <LabelPrinter
            printQueue={printQueue}
            removeFromPrintQueue={removeFromPrintQueue}
            setPrintQueue={setPrintQueue}
          />
        );
      case AppView.USER_MANAGEMENT:
        return <UserManagement />;
      case AppView.POS_HISTORY:
        return <PosHistory onClose={() => setCurrentView(AppView.DASHBOARD)} />;
      case AppView.SUSPENDED_CARTS:
        return (
          <SuspendedCarts
            onClose={() => setCurrentView(AppView.DASHBOARD)}
            onResume={(cartItems) => {
              // Betöltjük a kosarat a ScannerInterface-be
              setScannerState((prev) => ({
                ...prev,
                cart: cartItems,
              }));
              setCurrentView(AppView.DASHBOARD);
              toast.success('Kosár betöltve! Folytathatod a vásárlást.');
            }}
          />
        );
      case AppView.BOSS_DASHBOARD:
        // Protected route: Check company settings and user permissions
        const hasBossDashboardAccess =
          permissions?.hasBossDashboard &&
          (userProfile?.access_boss_dashboard || isAdmin || userProfile?.role === 'admin');

        if (!hasBossDashboardAccess) {
          return (
            <div className="max-w-7xl mx-auto p-4 md:p-8 flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
                  Hozzáférés megtagadva
                </h2>
                <p className="text-slate-600 dark:text-gray-400 mb-6">
                  Ehhez az oldalhoz vezetői jogosultság szükséges.
                </p>
                <button
                  onClick={() => setCurrentView(AppView.DASHBOARD)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                >
                  Vissza a főoldalra
                </button>
              </div>
            </div>
          );
        }
        return (
          <BossDashboard
            userProfile={userProfile}
            permissions={permissions}
            isAdmin={isAdmin}
            setView={setCurrentView}
          />
        );
      default:
        return (
          <Dashboard
            setView={setCurrentView}
            supplierData={supplierData}
            inventoryData={inventoryData}
            setInventoryData={setInventoryData}
            onDelete={handleDeleteItem}
            onUpdate={handleUpdateItem}
            addToPrintQueue={addToPrintQueue}
            currentPage={currentPage}
            totalItems={totalItems}
            goToNextPage={goToNextPage}
            goToPrevPage={goToPrevPage}
            pageSize={pageSize}
            isAdmin={isAdmin}
            permissions={permissions}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterMode={filterMode}
            setFilterMode={setFilterMode}
            fetchAllItemsForPrint={fetchAllItemsForPrint}
          />
        );
    }
  };

  return (
    <ErrorBoundary>
      <div
        className="min-h-screen flex flex-col text-slate-900 dark:text-gray-100 font-sans"
        style={{ backgroundColor: 'var(--color-background, #f8fafc)' }}
      >
        <Toaster position="top-right" />
        {/* DEBUG PANEL - Toggle with Ctrl+D - Only visible in development */}
        {showDebug && import.meta.env.DEV && (
          <div className="fixed top-0 left-0 right-0 bg-gray-900 text-green-400 text-xs p-4 font-mono z-[9999] border-b-2 border-green-500 shadow-2xl max-h-[50vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3 border-b border-gray-700 pb-2">
              <h3 className="text-yellow-400 font-bold text-sm uppercase">
                🔧 Debug Panel (Ctrl+D to toggle)
              </h3>
              <button
                onClick={() => setShowDebug(false)}
                className="text-red-400 hover:text-red-300 px-2 py-1 rounded"
              >
                ✕ Close
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <span className="text-gray-500">Role:</span>
                <span
                  className={`ml-2 ${debugRole === 'admin' ? 'text-yellow-400' : 'text-green-400'}`}
                >
                  {debugRole || 'null'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">IsAdmin:</span>
                <span className={`ml-2 ${isAdmin ? 'text-green-400' : 'text-red-400'}`}>
                  {isAdmin ? 'YES' : 'NO'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Session:</span>
                <span className={`ml-2 ${session ? 'text-green-400' : 'text-red-400'}`}>
                  {session ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Network:</span>
                <span
                  className={`ml-2 ${networkStatus.isOnline ? 'text-green-400' : 'text-red-400'}`}
                >
                  {networkStatus.isOnline ? 'ONLINE' : 'OFFLINE'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Connection Quality:</span>
                <span
                  className={`ml-2 ${
                    networkStatus.connectionQuality === 'good'
                      ? 'text-green-400'
                      : networkStatus.connectionQuality === 'poor'
                        ? 'text-yellow-400'
                        : 'text-red-400'
                  }`}
                >
                  {networkStatus.connectionQuality.toUpperCase()}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Queue (New):</span>
                <span className={`ml-2 ${queueLength > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {queueLength}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Syncing:</span>
                <span className={`ml-2 ${isSyncingNew ? 'text-yellow-400' : 'text-green-400'}`}>
                  {isSyncingNew ? 'YES' : 'NO'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Schema:</span>
                <span className="ml-2 text-blue-400">
                  {import.meta.env.VITE_SUPABASE_SCHEMA || 'not set'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Current View:</span>
                <span className="ml-2 text-cyan-400">{currentView}</span>
              </div>
              <div>
                <span className="text-gray-500">Git Commit:</span>
                <span className="ml-2 text-purple-400">
                  {import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || 'N/A'}
                </span>
              </div>
              {debugError && (
                <div className="col-span-full">
                  <span className="text-gray-500">Error:</span>
                  <span className="ml-2 text-red-400 break-all">{debugError}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <Navbar
          currentView={currentView}
          setView={setCurrentView}
          isDarkMode={isDarkMode}
          toggleDarkMode={toggleDarkMode}
          printQueueCount={printQueue.length}
          permissions={permissions}
          isAdmin={isAdmin}
          userProfile={userProfile}
          companyBranding={companyBranding}
        />

        {/* Version Update Banner */}
        {isNewVersionAvailable && <VersionUpdateBanner />}

        {/* Offline Mode & Sync Status Banners */}
        {session && (
          <>
            {/* NEW: Use new hook for offline status */}
            {!networkStatus.isOnline && (
              <div
                className={`bg-red-600 text-white px-4 py-2 text-center font-medium text-sm shadow-md ${
                  isNewVersionAvailable ? 'mt-16' : ''
                }`}
              >
                ⚠️ OFFLINE MODE - Az adatok helyi tárolásra kerülnek (IndexedDB), és automatikusan
                szinkronizálódnak, amikor a kapcsolat helyreáll.
              </div>
            )}
            {/* NEW: Use new hook for sync status */}
            {networkStatus.isOnline && isSyncingNew && (
              <div
                className={`bg-blue-500 text-white px-4 py-2 text-center font-medium text-sm shadow-md flex items-center justify-center gap-2 ${
                  isNewVersionAvailable ? 'mt-16' : ''
                }`}
              >
                <Loader2 className="animate-spin" size={16} />
                🔄 Szinkronizálás folyamatban...
              </div>
            )}
            {/* NEW: Show queue badge if items pending */}
            {networkStatus.isOnline && !isSyncingNew && queueLength > 0 && (
              <div
                className={`bg-yellow-500 text-white px-4 py-2 text-center font-medium text-sm shadow-md ${
                  isNewVersionAvailable ? 'mt-16' : ''
                }`}
              >
                ⏳ {queueLength} tétel vár szinkronizálásra
              </div>
            )}
          </>
        )}

        <main className="flex-1 w-full pb-20 md:pb-6">{renderView()}</main>
        {session && <SupportModal />}

        {/* Print Quantity Dialog */}
        {printQuantityDialog.isOpen && printQuantityDialog.ean && (
          <PrintQuantityDialog
            isOpen={printQuantityDialog.isOpen}
            productName={supplierData[printQuantityDialog.ean]?.name || 'Ismeretlen termék'}
            defaultQuantity={66}
            onConfirm={handlePrintQuantityConfirm}
            onCancel={() => setPrintQuantityDialog({ isOpen: false, ean: null })}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default App;
